import { baseApi, rtkq, asky, RtkqRequest, Http } from '@app/base';
import {
  GetEncounterTemplateDocument,
  GetEncounterTemplateRootsDocument,
  GetEncounterTemplatesDocument,
} from './graphql';
import {
  CreateEncounterTemplateRequest,
  EncounterTemplate,
  MoveEncounterTemplateRequest,
  UpdateEncounterTemplateNoteRequest,
  UpdateEncounterTemplatePathRequest,
} from './encounterTemplatesTypes';
import qs from 'qs';

export const encounterTemplatesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllEncounterTemplateRoots: build.query<
      string[],
      RtkqRequest<{ onlyCommon?: boolean; providerId?: string }>
    >({
      queryFn: rtkq.graphql(GetEncounterTemplateRootsDocument, {
        vars: ({ providerId, onlyCommon }) => {
          const filter = asky.filter.json([
            !!providerId && { fieldId: 'providerId', operator: '=', value: providerId },
            !!onlyCommon && { fieldId: 'providerId', operator: '=', value: undefined },
          ]);

          return { filter };
        },
        select: (x) => x.encounterTemplateRoots as string[],
      }),
      providesTags: (result = []) => [
        { type: 'medical-record:encounter-template-collection', id: 'list' },
        ...result.map((x) => ({
          type: 'medical-record:encounter-template-collection' as const,
          id: x,
        })),
      ],
    }),

    getAllEncounterTemplates: build.query<
      EncounterTemplate[],
      RtkqRequest<{ root?: string; onlyCommon?: boolean; providerId?: string }>
    >({
      queryFn: rtkq.graphql(GetEncounterTemplatesDocument, {
        select: (x) => x.encounterTemplates as EncounterTemplate[],
        vars: ({ root, providerId, onlyCommon }) => {
          const filter = asky.filter.json([
            !!root && { fieldId: 'root', operator: '=', value: root },
            !!providerId && { fieldId: 'providerId', operator: '=', value: providerId },
            !!onlyCommon && { fieldId: 'providerId', operator: '=', value: undefined },
          ]);

          return { filter };
        },
      }),
      providesTags: (result = []) => [
        { type: 'medical-record:encounter-template', id: 'list' },
        ...result.flatMap(({ id }) => ({
          type: 'medical-record:encounter-template' as const,
          id: id,
        })),
        ...result.flatMap(({ root }) => ({
          type: 'medical-record:encounter-template-collection' as const,
          id: root,
        })),
      ],
    }),

    getEncounterTemplate: build.query<EncounterTemplate, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetEncounterTemplateDocument, {
        select: (x) => x.encounterTemplate as EncounterTemplate,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (_, __, { id }) => [
        {
          type: 'medical-record:encounter-template' as const,
          id: id,
        },
      ],
    }),

    createEncounterTemplate: build.mutation<string, RtkqRequest<CreateEncounterTemplateRequest>>({
      queryFn: rtkq.axios((args) => Http.post('/api/encounter-template', args)),
      invalidatesTags: (_, __, { root }) => [
        { type: 'medical-record:encounter-template', id: 'list' },
        { type: 'medical-record:encounter-template-collection', id: root },
        { type: 'medical-record:encounter-template-collection', id: 'list' },
      ],
    }),

    updateEncounterTemplate: build.mutation<string, RtkqRequest<UpdateEncounterTemplateNoteRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/encounter-template/${args.id}`, args)),
      invalidatesTags: (_, __, { id }) => [{ type: 'medical-record:encounter-template', id }],
    }),

    deleteEncounterTemplate: build.mutation<string, RtkqRequest<{ id: string; root: string }>>({
      queryFn: rtkq.axios(({ id }) => Http.delete(`/api/encounter-template/${id}`)),
      invalidatesTags: (_, __, { id, root }) => [
        { type: 'medical-record:encounter-template', id: id },
        { type: 'medical-record:encounter-template-collection', id: root },
      ],
    }),

    updateEncounterTemplatePath: build.mutation<string, RtkqRequest<UpdateEncounterTemplatePathRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/encounter-template/path`, args)),
      invalidatesTags: (_, __, { newPath, oldPath }) => [
        { type: 'medical-record:encounter-template', id: 'list' },
        { type: 'medical-record:encounter-template-collection', id: newPath.root },
        { type: 'medical-record:encounter-template-collection', id: oldPath.root },
      ],
    }),

    deleteEncounterTemplates: build.mutation<string, RtkqRequest<{ root: string; directory?: string }>>({
      queryFn: rtkq.axios((args) => {
        return Http.delete(`/api/encounter-template?${qs.stringify(args)}`);
      }),
      invalidatesTags: (_, __, { root }) => [
        { type: 'medical-record:encounter-template-collection', id: root },
        { type: 'medical-record:encounter-template', id: 'list' },
      ],
    }),

    moveEncounterTemplate: build.mutation<string, RtkqRequest<MoveEncounterTemplateRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/encounter-template/${args.id}/move`, args)),
      invalidatesTags: (_, __, { id, newPath, root }) => [
        { type: 'medical-record:encounter-template', id: id },
        { type: 'medical-record:encounter-template-collection', id: root },
        { type: 'medical-record:encounter-template-collection', id: newPath.root },
      ],
    }),
  }),
});

export const {
  useGetAllEncounterTemplateRootsQuery,
  useGetEncounterTemplateQuery,
  useCreateEncounterTemplateMutation,
  useUpdateEncounterTemplateMutation,
  useDeleteEncounterTemplateMutation,
  useUpdateEncounterTemplatePathMutation,
  useDeleteEncounterTemplatesMutation,
  useMoveEncounterTemplateMutation,
} = encounterTemplatesApi;
